import React, { FC, ReactNode, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Sidebar } from "../Sidebar";
import { BottomSheet, Button, Icon } from "../../../components/common";
import { AvatarDropdown } from "../../../components/menu";
import { selectCurrentUser } from "../../../store/reducers/user";
import { AccountType } from "../../../types/UserTypes";
import { showCookieConsentAction } from "../../../utils/dom";
import { isMobileMedia } from "../../../utils/media";
import { KycModal } from "../../../components/modals/kycModal";
import { NewBankAccountModal } from "../../../components/modals/NewBankAccountModal";
import { toastSuccess } from "../../../utils/toasts";
import { Wallet } from "../../../types/WalletTypes";
import { useGetWalletsQuery } from "../../../store/services/wallets";
import { selectWallets } from "../../../store/reducers/wallets";

type Props = {
  title?: ReactNode;
  action?: ReactNode;
  onSelectWallet?: (wallet: Wallet) => void;
};

export const Header: FC<Props> = ({ title, action, onSelectWallet }) => {
  const {
    account_type,
    first_name,
    invoicing_data,
    // is_onmeta_eligible,
    // is_usd_eligible,
    kyc_complete,
  } = useSelector(selectCurrentUser);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dispatch = useDispatch();

  //const isBusiness = account_type === AccountType.BUSINESS;

  const seenKycModal = () => {
    const result = sessionStorage.getItem("seenKycModal");
    if (result === null) {
      return false;
    }
    return result === "true";
  };
  const setSeenKycModal = () => {
    sessionStorage.setItem("seenKycModal", "true");
  };

  const showKycModalFirstLoad: boolean = !kyc_complete;
  const [showKycModal, setShowKycModal] = useState(
    showKycModalFirstLoad && !seenKycModal() ? true : false
  );

  const seenBankAccountModal = () => {
    const result = sessionStorage.getItem("seenBankAccountModal");
    if (result === null) {
      return false;
    }
    return result === "true";
  };
  const setSeenBankAccountModal = () => {
    sessionStorage.setItem("seenBankAccountModal", "true");
  };

  const { isLoading: isLoadingWallets } = useGetWalletsQuery();
  const walletList = useSelector(selectWallets);
  const hasWallets = Array.isArray(walletList) && walletList.length > 0;

  const [showBankAccountModal, setShowBankAccountModal] = useState(false);

  useEffect(() => {
    if (!isLoadingWallets) {
      const shouldShowModal =
        !hasWallets && !seenBankAccountModal() && !showKycModal;
      setShowBankAccountModal(shouldShowModal);
    }
  }, [isLoadingWallets, hasWallets, showKycModal]);

  useEffect(() => {
    // Shows the cookie consent button when the menu is open on mobile
    // As the menu is in a bottom sheet component, the Intercom and VideoAsk
    // buttons won't be visible when opened.
    if (isMobileMedia()) {
      showCookieConsentAction(isMenuOpen);
    }
  }, [dispatch, isMenuOpen]);

  return (
    <>
      <header className="sticky md:relative top-0 left-0 right-0 p-4 md:px-0 md:py-8 flex flex-col gap-5 !z-[25]">
        <div className="flex justify-between">
          {/* Mobile Only */}
          <div className="md:hidden">
            <Button
              className="p-0"
              icon
              onClick={() => setIsMenuOpen(true)}
              variant={Button.variant.PRIMARY}
            >
              <div className="text-sm">
                <Icon icon="Menu" size={24} className="white" />
              </div>
            </Button>
          </div>

          {/* Mobile Only */}
          <h1 className="md:hidden flex justify-center items-center font-poppins font-bold text-2xl	text-gray-500">
            {title}
          </h1>

          {/* Desktop Only */}
          <div className="hidden md:flex flex-row items-center justify-between pt-6">
            <h1 className="inline-block font-poppins font-bold text-5xl text-gray-500">
              {title}
            </h1>

            <div className="ml-auto">{action}</div>
          </div>

          <AvatarDropdown
            title={
              account_type === AccountType.CONTRACTOR
                ? first_name
                : invoicing_data?.company_name || ""
            }
          />
        </div>

        {/* Mobile Only - GUy HACK! */}

        <div>
          <KycModal
            isOpen={showKycModal}
            close={() => {
              setShowKycModal(false);
              setSeenKycModal();
            }}
            showExtraText={true}
          />
        </div>
        <div>
          <NewBankAccountModal
            isOpen={showBankAccountModal}
            close={() => {
              setShowBankAccountModal(false);
              setSeenBankAccountModal();
            }}
            callback={(data) => {
              toastSuccess("Bank account created successfully");
              setShowBankAccountModal(false);
              setSeenBankAccountModal();
              if (onSelectWallet && data) {
                onSelectWallet(data);
              }
            }}
            showExtraText={true}
          />
        </div>
      </header>

      <BottomSheet
        isOpen={isMenuOpen}
        close={() => setIsMenuOpen(false)}
        className="!bg-purple-50"
      >
        <Sidebar
          className="h-[85dvh] mb-6"
          onClick={() => setIsMenuOpen(false)}
        />
      </BottomSheet>
    </>
  );
};
